// Ghost controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import type { Snapshot } from '../../core';
import { GHOST_BALANCE_VERSION } from '../registryKeys';
import { destroyGhostMarkers } from '../ghostOverlay';
import { buildGhostTrack, encodeTrack, decodeTrack } from '../ghostTrack';
import { readLastGhost, writeLastGhost } from '../lastGhost';
import { GhostRacer } from '../ghostRacer';
import { getGhost } from '../../net/ghostClient';
import { utcDayKey } from '../stats';
import { PLAYER } from './types';
import type { GameScene } from '../scenes/GameScene';


  /**
   * GHOST DUELS Slice A — decode the LOCAL saved daily ghost iff this run is that exact daily board.
   * The pin requires the same balance version AND the same daily identity (seed + mutator + topic), so
   * the recorded track replays only against the board it was captured on; a different day (different
   * topic/mutator) or a stale bv silently yields NO ghost and the run plays normally (fail-open to play).
   */
export function resolvePendingGhost(this: GameScene): void {
    this.pendingGhostTrack = null;
    if (!this.isDaily) {
      this.ladderRace = null;
      return;
    }
    // GHOST DUELS Slice C — a ladder challenger passed from the panel takes precedence over the self-race.
    // Same pin as every other ghost path (bv + daily identity); a miss/decode-fail fails OPEN: null the
    // ladder race (so no phantom rung verdict lands at Result) and fall through to the self-race.
    if (this.ladderRace) {
      const rec = this.ladderRace.record;
      const pinned =
        rec.bv === GHOST_BALANCE_VERSION && rec.seed === this.seed && rec.m === this.dailyMutatorId && rec.topic === this.topic;
      const track = pinned ? decodeTrack(rec.track) : null;
      if (track && track.events.length > 0) {
        this.pendingGhostTrack = track;
        this.ghostName = (rec.name || 'GHOST').slice(0, 12);
        return;
      }
      this.ladderRace = null;
    }
    if (this.ghostSource !== 'last-daily') return;
    const g = readLastGhost();
    if (!g) return;
    if (g.bv !== GHOST_BALANCE_VERSION || g.seed !== this.seed || g.m !== this.dailyMutatorId || g.topic !== this.topic) {
      return;
    }
    const track = decodeTrack(g.track);
    if (!track || track.events.length === 0) return;
    this.pendingGhostTrack = track;
    this.ghostName = g.name || 'YOUR GHOST';
  }


  /**
   * GHOST DUELS Slice B — fetch a friend's KV-stored ghost (&gr=<id>) and, if its pin matches THIS exact
   * daily board (bv/seed/mutator/topic — identical gate to the Slice A local self-race), decode + build the
   * spectral racer under the blob's name. FAIL-CLOSED: no id / a cold-or-missing store / a bv-or-pin miss /
   * a corrupt track all just return, leaving the existing static-footprint path (&ghost overlay) untouched —
   * the run never breaks. Async (the fetch resolves after create()); guarded so a scene swap or an
   * already-built ghost can't double-build, and the redundant static overlay is retired on a live hit.
   */
export function fetchRemoteGhost(this: GameScene, snap: Snapshot): void {
    if (!this.ghostRemoteId || !this.isDaily || this.boardIndex !== 0) return;
    const id = this.ghostRemoteId;
    void getGhost(id).then((rec) => {
      if (!rec) return;
      // Scene moved on / a ghost already exists (e.g. the local self-race won the race) → drop this fetch.
      if (this.ended || this.ghostRacer || !this.scene.isActive()) return;
      // Pin EXACTLY like the local self-race: same balance version AND same daily identity.
      if (rec.bv !== GHOST_BALANCE_VERSION || rec.seed !== this.seed || rec.m !== this.dailyMutatorId || rec.topic !== this.topic) {
        return;
      }
      const track = decodeTrack(rec.track);
      if (!track || track.events.length === 0) return;
      this.pendingGhostTrack = track;
      this.ghostName = (rec.name || 'GHOST').slice(0, 12);
      const racer = this.buildGhostRacer(snap);
      // The fetch resolved after the board already advanced — fast-forward the racer to the LIVE sim time
      // (instant glyphs/position, no pop/spark burst) so it appears already mid-race, not replaying from 0.
      racer?.catchUp(this.sim.snapshot().timeMs);
      // A live racer replays the same run the &ghost static overlay would paint, so retire the redundant
      // footprints to avoid double-marking the recipient's board.
      if (this.ghostMarkers.size > 0) {
        destroyGhostMarkers(this, this.ghostMarkers);
        this.ghostMarkers = new Map();
      }
    });
  }


  /**
   * GHOST DUELS Slice A — build the spectral self-race replay from the pin-resolved track. The ghost
   * shares the rival sheet with a spectral skin (attachMuncherSprite falls back to the geometric rival)
   * and starts on the live human start cell (identical on the pinned same-layout board). No-op when
   * there is no pending ghost. It is a pure renderer entity — it never touches the Sim.
   */
export function buildGhostRacer(this: GameScene, snap: Snapshot): GhostRacer | undefined {
    if (!this.pendingGhostTrack) return undefined;
    const startCell = snap.players.find((p) => p.id === PLAYER)?.pos ?? 0;
    this.ghostRacer = new GhostRacer(this, {
      track: this.pendingGhostTrack,
      name: this.ghostName,
      startCell,
      cellCenter: (id) => this.cellCenter(id),
      spriteYOffset: this.spriteYOffset(),
      munchRadius: this.munchRadius(),
      cellW: this.cellW,
      cellH: this.cellH,
      chipAnchor: this.ghostChipAnchor,
      attachRaster: (c, skin) => this.attachMuncherSprite(c, 'rival', skin),
    });
    return this.ghostRacer;
  }


  /**
   * GHOST DUELS Slice A — persist THIS daily board's player-0 event track as the local self-race ghost
   * (`trivia.lastGhost.v1`). DAILY board-0 runs only; once-guarded; tolerant persist. This is a pure
   * observed-event extraction (never a re-sim) so it is determinism-independent (GHOST_DUELS_BRIEF §1).
   */
export function recordDailyGhost(this: GameScene, snap: Snapshot): void {
    if (this.ghostRecorded) return;
    this.ghostRecorded = true;
    if (!this.isDaily || this.boardIndex !== 0) return;
    const track = buildGhostTrack(this.sim.log.all());
    if (track.events.length === 0) return;
    writeLastGhost({
      v: 1,
      bv: GHOST_BALANCE_VERSION,
      day: utcDayKey(),
      topic: this.topic,
      seed: this.seed,
      m: this.dailyMutatorId,
      name: 'YOUR GHOST',
      // Advertise the TRACK's munch-sum, not human.score: the racing chip replays exactly the
      // recorded munch values, so CTA target and live chip always agree (review #2 — human.score
      // can exceed the track by perfectBoardBonus, which the replay can never show).
      score: track.finalScore,
      durationMs: Math.round(snap.timeMs),
      track: encodeTrack(track),
    });
  }
